// MACHINERY V2 (Stroje) — Špecifikácie tab (M2.5).
//
// Spec: data package §3 + ticket §5 #3 / §7 fix 5. Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (`SpecsTab`); artboards
// 1006 (desktop), 1007 (the catalog-vs-custom tab-row diff), 1015 (phone).
// Rules = specSheet.ts, resolution = specResolve.ts, pixels = SpecSectionsV2 /
// specChrome / SpecDescription.
//
// THE TAB ONLY EXISTS FOR A CATALOG-LINKED MACHINE, and that gate is NOT here.
// profileTabContract.ts:`buildProfileTabs` pushes "specifications" only when
// `hasCatalogModel`, so a custom machine never renders this component at all —
// "the tab is not rendered (never rendered-empty)", §5 catalog-vs-custom rule,
// drawn in artboard 1007. Re-testing it here would be a second place to get it
// wrong.
//
// THE FETCH IS DE-DUPLICATED, NOT UNCONDITIONAL. `selectedModel` is shared with
// the catalog pages, so v1 skips the dispatch when the slot already holds this
// model (MachineSpecs.tsx:908-916) and deliberately does NOT clearSelectedModel
// on unmount — the catalog detail page owns that lifecycle, and clearing here
// caused a "Model not found" race. Both behaviours are kept.
//
// RENDERING IS ALSO ID-GUARDED, which v1 is not. Because the slot is shared, an
// earlier catalog visit can leave a DIFFERENT model in it, and v1 renders that
// model's spec sheet under this machine until the new fetch lands. The guard
// (`selectedModel.id === catalogModelId`) is the same one MachineProfileV2
// applies to `customerMachinery.machine` for exactly this reason.
//
// FIX 5 (specs half): `getCatalogModelDetail.rejected` sets ONLY
// `selectedModelLoading = false` (machineCatalogSlice.ts:136-138) — there is no
// error field to read, so a failed fetch renders a blank tab forever. The
// failure is tracked here, from the dispatch's own promise.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { getCatalogModelDetail } from "../../../machineCatalogSlice";
import RetryNotice from "../../feedback/RetryNotice";
import { SkeletonList, TabCard, TabEmpty } from "./tabAtoms";
import { resolveSpecSheet } from "./specResolve";
import { getSectionGroupKey } from "./specSheet";
import { GROUP_LABEL_KEYS, GroupDivider } from "./specChrome";
import { EquipmentSection, SpecSection } from "./SpecSectionsV2";
import { SpecDescription } from "./SpecDescription";

export interface SpecificationsTabV2Props {
  catalogModelId: string;
}

const SpecificationsTabV2: React.FC<SpecificationsTabV2Props> = ({ catalogModelId }) => {
  const dispatch = useAppDispatch();
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const { isPhone } = useBreakpoint();

  const selectedModel = useAppSelector((s) => s.machineCatalog.selectedModel);
  const loading = useAppSelector((s) => s.machineCatalog.selectedModelLoading);

  const [failed, setFailed] = useState(false);
  const [metric, setMetric] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    setFailed(false);
    dispatch(getCatalogModelDetail(catalogModelId))
      .unwrap()
      .catch(() => setFailed(true));
  }, [dispatch, catalogModelId]);

  useEffect(() => {
    setCollapsed(new Set());
    if (selectedModel?.id === catalogModelId) {
      setFailed(false);
      return;
    }
    load();
    // `selectedModel?.id` is read, not depended on: re-running when the slot
    // changes is exactly the loop the de-dup guard exists to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogModelId, load]);

  const currentLang = getCurrentLanguage();
  const model = selectedModel?.id === catalogModelId ? selectedModel : undefined;

  const sheet = useMemo(
    () => resolveSpecSheet(model?.specSections, currentLang),
    [model, currentLang]
  );

  const hasContent =
    sheet.primaryText.length > 0 ||
    sheet.machineTypeDescriptions.length > 0 ||
    sheet.regularSections.length > 0 ||
    sheet.equipmentSections.length > 0;

  // Sections start EXPANDED (v1 seeds the expanded set with every section), so
  // the local state tracks the exceptions instead of the members — no effect,
  // no initialisation race, and a newly arrived section is open like the rest.
  const isOpen = (name: string) => !collapsed.has(name);
  const toggle = (name: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const allExpanded = collapsed.size === 0;
  const toggleAll = () =>
    setCollapsed(allExpanded ? new Set(sheet.regularSections.map((s) => s.sectionName)) : new Set());

  /** One divider per group, at its first section — v1's `renderedGroups`. */
  const rendered = new Set<string>();
  const dividerFor = (sectionName: string) => {
    const key = getSectionGroupKey(sectionName);
    if (!key || rendered.has(key)) return null;
    rendered.add(key);
    return <GroupDivider label={translate(GROUP_LABEL_KEYS[key])} groupKey={key} />;
  };

  const segment = (label: string, on: boolean, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        padding: "3px 10px",
        borderRadius: 5,
        cursor: "pointer",
        background: on ? "var(--card)" : "transparent",
        color: on ? "var(--mod-accent)" : "var(--faint)",
        fontWeight: on ? 600 : 400,
        border: "1px solid " + (on ? "var(--border)" : "transparent"),
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--faint)",
            letterSpacing: "0.04em",
            minWidth: 0,
          }}
        >
          {translate("myMachinery:v2.tabs.specs.intro")}
        </span>
        <span style={{ flex: 1 }} />
        {hasContent && sheet.regularSections.length > 0 && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={toggleAll}
            style={{ height: 26, fontSize: 11.5, whiteSpace: "nowrap" }}
          >
            {translate(allExpanded ? "myMachinery:collapseAll" : "myMachinery:expandAll")}
          </button>
        )}
        <div
          style={{
            display: "inline-flex",
            gap: 2,
            padding: 2,
            background: "var(--bg-soft)",
            border: "1px solid var(--border)",
            borderRadius: 7,
          }}
        >
          {segment(translate("myMachinery:metric"), metric, () => setMetric(true))}
          {segment(translate("myMachinery:imperial"), !metric, () => setMetric(false))}
        </div>
      </div>

      {failed && (
        <RetryNotice
          title={translate("myMachinery:v2.tabs.specs.loadFailed")}
          hint={translate("myMachinery:v2.tabs.loadFailedHint")}
          onRetry={load}
          busy={loading}
        />
      )}

      {loading && !model && (
        <TabCard>
          <SkeletonList rows={4} height={38} />
        </TabCard>
      )}

      {/* §3: a loaded model with nothing to show says so; a model that never
          arrived (and did not fail) renders nothing at all, as v1 does. */}
      {!loading && !failed && model && !hasContent && (
        <TabEmpty title={translate("myMachinery:noSpecifications")} />
      )}

      {model && hasContent && (
        <>
          {sheet.primaryText && (
            <SpecDescription
              label={translate("myMachinery:specDescription")}
              text={sheet.primaryText}
              showEncodingFallback={sheet.usedEncodingFallback}
            />
          )}
          {sheet.machineTypeDescriptions.map((d) => (
            <SpecDescription key={d.label} label={d.label} text={d.text} />
          ))}

          {sheet.regularSections.map((section) => (
            <React.Fragment key={section.sectionName}>
              {dividerFor(section.sectionName)}
              <SpecSection
                section={section}
                open={isOpen(section.sectionName)}
                onToggle={() => toggle(section.sectionName)}
                toMetric={metric}
                phone={isPhone}
              />
            </React.Fragment>
          ))}

          {sheet.equipmentSections.length > 0 && dividerFor(sheet.equipmentSections[0].sectionName)}
          {sheet.equipmentSections.map((section) => (
            <EquipmentSection key={section.sectionName} section={section} phone={isPhone} />
          ))}
        </>
      )}
    </div>
  );
};

export default SpecificationsTabV2;
